import { useState, useContext, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DashboardLayout from "defaultComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "defaultComponents/Navbars/DashboardNavbar";
import { Grid, Tabs } from "@mui/material";
import Modal from "components/Modal/Modal";
import { RiArrowLeftLine } from "react-icons/ri";
import CommunityInfo from "./CommunityView/CommunityInfo";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import * as S from "./style";
import * as T from "components/Toast/style";
import { TokenContext } from "context/TokenContext";
import moment from "moment";
import "moment/locale/pt-br";
import { getCommunityById, getCalendarByCommunityId } from 'utils/requests/communities';
import BuildCalendar from "./forms/BuildCalendarForm";
import ContentCalendar from "./CommunityView/ContentCalendar";
import ScriptLibrary from "./CommunityView/ScriptLibrary";
import showToast from "components/Toast/Toast";
// import Medals from "./CommunityView/Medals";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#e0e0e0",
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 14,
  },
}));

const CommunityDetails = () => {
  moment.locale("pt-br");
  const navigate = useNavigate();
  const { communityId } = useParams();
  const [searchParams] = useSearchParams();

  const { decodedToken } = useContext(TokenContext);
  const role = decodedToken?.type;
  const redirects = {
    ADMINISTRATOR: "/comunidade",
  };

  const [communityData, setCommunityData] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [tabValue, setTabValue] = useState("general");
  const [isLoading, setIsLoading] = useState(true);
  const [openReprogramModal, setOpenReprogramModal] = useState(false);

  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  const fetchCommunities = async () => {
    setIsLoading(true);
    try {
      const data = await getCommunityById(communityId);
      setCommunityData(data);
    } catch (error) {
      if (error.status !== 404) {
        showToast('Erro ao buscar comunidade.', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }

  const fetchCalendar = async () => {
    try {
      const data = await getCalendarByCommunityId(communityId);
      setCalendarData(data);
    } catch (error) {
      if (error.status === 404) {
        setCalendarData(null);
      } else {
        showToast('Erro ao buscar calendário.', 'error');
      }
    }
  }

  const refreshData = async () => {
    await fetchCommunities();
    await fetchCalendar();
  }

  useEffect(() => {
    fetchCommunities();
    fetchCalendar();
  }, [communityId]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['general', 'calendar', 'routines', 'medals'].includes(tab)) {
      setTabValue(tab);
    }
  }, [searchParams]);

  return (
    <DashboardLayout>
      <DashboardNavbar 
        customTitle={communityData?.plan_details?.name || 'Comunidade'} 
      />
      <T.ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Grid container spacing={3}>
        <S.MainGrid item md={3} xl={3}>
          <S.ProjectCard sx={{ width: "100%" }}>
            <Grid container flexDirection="column" justifyContent="space-between" height="100%">
              <Grid container flexDirection="column">
                <S.BackGrid container gap="10px" onClick={() => navigate(redirects[role])}>
                  <RiArrowLeftLine size="20px" style={{ alignSelf: "center" }} />
                  <p>Voltar</p>
                </S.BackGrid>
                <S.TabsGrid>
                  <Tabs
                    orientation="vertical"
                    value={tabValue}
                    onChange={handleSetTabValue}
                    sx={{
                      background: "transparent",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                    TabIndicatorProps={{
                      style: {
                        borderRadius: "5px 5px 15px 5px",
                        backgroundColor: "rgba(239, 241, 246, 0.10)",
                        backdropFilter: "blur(15px)",
                      },
                    }}
                  >
                    <S.SideTabs
                      sx={{ paddingLeft: "20px !important" }}
                      label="Geral"
                      value="general"
                    />
                    <S.SideTabs
                      sx={{ paddingLeft: "20px !important" }}
                      label="Calendário"
                      value="calendar"
                    />
                    <S.SideTabs
                      sx={{ paddingLeft: "20px !important" }}
                      label="Roteiros"
                      value="routines"
                    />
                    <S.SideTabs
                      sx={{ paddingLeft: "20px !important" }}
                      label="Medalhas"
                      value="medals"
                    />
                  </Tabs>
                </S.TabsGrid>
              </Grid>
            </Grid>
          </S.ProjectCard>
        </S.MainGrid>
        <S.ContentGrid item md={9} xl={9}>
          {isLoading ? (
            <div>
              Carregando...
            </div>
          ) : (
            <>
          {tabValue === "general" && (
            <CommunityInfo 
              data={communityData} 
              refetch={() => {
                refreshData();
              }}
              onDelete={() => {}}
            />
          )}
          {tabValue === "calendar" && (
            <>
              {calendarData && Object.keys(calendarData).length > 0 ? (
                <ContentCalendar
                  data={communityData}
                  calendarData={calendarData}
                  refetch={() => {
                    refreshData();
                  }}
                  onEditCalendar={() => setOpenReprogramModal(true)}
                />
              ) : (
                <BuildCalendar 
                  communityData={communityData}
                  refetch={() => {
                    refreshData();
                  }} 
                />
              )}
              {openReprogramModal && (
                <Modal open={openReprogramModal} size="lg" onClose={() => setOpenReprogramModal(false)}>
                  <BuildCalendar
                    communityData={communityData}
                    refetch={() => {
                      refreshData();
                      setOpenReprogramModal(false);
                    }}
                    isReprogramming={true}
                    calendarId={calendarData?.calendars?.[0]?.id || calendarData?.[0]?.id}
                    onSubmitCalendarSuccess={() => setOpenReprogramModal(false)}
                  />
                </Modal>
              )}
            </>
          )}
          {tabValue === "routines" && (
            <ScriptLibrary />
          )} 
          {/*
          {tabValue === "medals" && (
            <Medals />
          )} */}
          </>
          )}
        </S.ContentGrid>
      </Grid>
    </DashboardLayout>
  );
};

export default CommunityDetails;